import * as React from 'react';
import {
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import { AdsTxtAccountType, type AdsTxtRecordPayload, type AdsTxtReference } from '../../../model';

interface AdsTxtRecordFieldsProps {
  value: AdsTxtRecordPayload;
  demandPartners: readonly AdsTxtReference[];
  loadingDemandPartners: boolean;
  pending: boolean;
  lockIdentity: boolean;
  submitted: boolean;
  valid: boolean;
  onChange: (next: Partial<AdsTxtRecordPayload>) => void;
}

export function AdsTxtRecordFields({
  value,
  demandPartners,
  loadingDemandPartners,
  pending,
  lockIdentity,
  submitted,
  valid,
  onChange,
}: AdsTxtRecordFieldsProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field
        label={dashboardT('adsTxt.records.domain')}
        required
        error={submitted ? !/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(value.domain) : false}
      >
        <Input
          value={value.domain}
          disabled={pending || lockIdentity}
          onChange={(event) => {
            onChange({ domain: event.target.value });
          }}
        />
      </Field>
      <Field
        label={dashboardT('adsTxt.records.accountId')}
        required
        error={submitted ? !value.publisherAccountId.trim() : false}
      >
        <Input
          value={value.publisherAccountId}
          disabled={pending || lockIdentity}
          onChange={(event) => {
            onChange({ publisherAccountId: event.target.value });
          }}
        />
      </Field>
      <Field label={dashboardT('adsTxt.records.accountType')} required>
        <Select
          value={value.accountType}
          disabled={pending || lockIdentity}
          onValueChange={(accountType: AdsTxtAccountType) => {
            onChange({ accountType });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(AdsTxtAccountType).map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field
        label={dashboardT('adsTxt.records.demandPartner')}
        required
        error={submitted ? !value.demandPartnerId : false}
      >
        <Select
          value={value.demandPartnerId}
          disabled={pending || lockIdentity || loadingDemandPartners}
          onValueChange={(demandPartnerId) => {
            onChange({ demandPartnerId });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={dashboardT('adsTxt.records.selectDemandPartner')} />
          </SelectTrigger>
          <SelectContent>
            {demandPartners.map((partner) => (
              <SelectItem key={partner.id} value={partner.id}>
                {partner.displayName ?? partner.name ?? partner.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label={dashboardT('adsTxt.records.certAuthority')}>
        <Input
          value={value.certAuthority ?? ''}
          disabled={pending}
          onChange={(event) => {
            onChange({ certAuthority: event.target.value || null });
          }}
        />
      </Field>
      <Field label={dashboardT('adsTxt.records.priority')}>
        <Select
          value={value.priority?.toString() ?? 'none'}
          disabled={pending}
          onValueChange={(priority) => {
            onChange({ priority: priority === 'none' ? null : Number(priority) });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            {[1, 2, 3, 4, 5].map((priority) => (
              <SelectItem key={priority} value={priority.toString()}>
                {priority}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label={dashboardT('adsTxt.records.comment')}>
        <Input
          value={value.comment ?? ''}
          disabled={pending}
          onChange={(event) => {
            onChange({ comment: event.target.value || null });
          }}
        />
      </Field>
      <Field label={dashboardT('adsTxt.records.note')}>
        <Textarea
          value={value.note ?? ''}
          disabled={pending}
          onChange={(event) => {
            onChange({ note: event.target.value || null });
          }}
        />
      </Field>
      <div className="flex flex-wrap gap-5 sm:col-span-2">
        {(['web', 'ctv'] as const).map((key) => (
          <label key={key} className="flex items-center gap-2 text-sm font-medium">
            <Checkbox
              checked={value[key]}
              disabled={pending}
              onCheckedChange={(next) => {
                onChange({ [key]: next === true });
              }}
            />
            {key === 'web' ? 'Web' : 'CTV'}
          </label>
        ))}
      </div>
      {submitted && !valid ? (
        <p role="alert" className="text-destructive text-xs sm:col-span-2">
          {dashboardT('adsTxt.records.validationError')}
        </p>
      ) : null}
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-2 text-sm font-medium">
      <span>
        {label}
        {required ? ' *' : ''}
      </span>
      {children}
      {error ? (
        <span className="text-destructive block text-xs">{dashboardT('adsTxt.required')}</span>
      ) : null}
    </label>
  );
}
